import { createContext, type ComponentChildren } from 'preact';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { useAuth } from '../lib/auth';
import { getProfile, type Profile } from '../lib/profile';

// Onboarding routing state, the web equivalent of the Flutter
// `signedInRouteProvider`:
//   loading     — auth or profile still resolving
//   signed-out  — no session
//   full        — signed in, no profile row → run full onboarding
//   welcomeBack — signed in, profile exists but newUser=0 → re-accept terms
//   ready       — signed in and onboarded
export type ProfileRoute = 'loading' | 'signed-out' | 'full' | 'welcomeBack' | 'ready';

type ProfileState = {
  route: ProfileRoute;
  profile: Profile | null;
  refresh: () => Promise<void>;
  /** Patch the cached profile in place without a re-fetch (avoids a shell remount). */
  mergePatch: (patch: Partial<Profile>) => void;
};

const Ctx = createContext<ProfileState>({
  route: 'loading',
  profile: null,
  refresh: async () => {},
  mergePatch: () => {},
});

export function ProfileProvider({ children }: { children: ComponentChildren }) {
  const { status, accessToken, hydrate } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loaded, setLoaded] = useState(false);

  // Read the token lazily so authedFetch's retry sees a freshly-hydrated value.
  const tokenRef = useRef<string | null>(accessToken);
  tokenRef.current = accessToken;
  const auth = useMemo(
    () => ({ getToken: () => tokenRef.current, hydrate }),
    [hydrate],
  );

  const refresh = useCallback(async () => {
    try {
      const p = await getProfile(auth);
      setProfile(p);
    } catch {
      setProfile(null);
    } finally {
      setLoaded(true);
    }
  }, [auth]);

  useEffect(() => {
    if (status === 'signed-in') {
      setLoaded(false);
      refresh();
    } else if (status === 'signed-out') {
      setProfile(null);
      setLoaded(true);
    }
  }, [status]);

  const mergePatch = useCallback((patch: Partial<Profile>) => {
    setProfile((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const route: ProfileRoute = useMemo(() => {
    if (status === 'unknown') return 'loading';
    if (status === 'signed-out') return 'signed-out';
    if (!loaded) return 'loading';
    if (!profile) return 'full';
    return profile.newUser === 1 ? 'ready' : 'welcomeBack';
  }, [status, loaded, profile]);

  return (
    <Ctx.Provider value={{ route, profile, refresh, mergePatch }}>{children}</Ctx.Provider>
  );
}

export function useProfile() {
  return useContext(Ctx);
}
